import {
  ApiError,
  formatMoney,
  formatUsPhone,
  queryKeys,
  RENTAL_BILLING_INTERVALS,
  type RentalCardIntent,
  type RentalReturnScan,
  type RentalVerifyScanResult,
  rentalFeePeriods,
  rentalIntervalLabel,
  rentalIntervalUnit,
  UNIT_IDENTIFIER_LABELS,
  unitIdentifierKind,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  humanizeStatus,
  Input,
  RENTAL_STATUS_TONE,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  MoreVertical,
  Package,
  Pencil,
  Plus,
  ScanLine,
  Trash2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { MarkImportantButton } from '../components/mark-important.js'
import { RecordPaymentDialog } from '../components/record-rental-payment-dialog.js'
import { NewCardPanel, ReaderPanel, SavedCardPanel } from '../components/rental-charge-panels.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface RentalPayment {
  id: string
  kind: 'fee' | 'deposit' | 'deposit_refund' | 'damage_fee'
  provider: string
  status:
    | 'pending'
    | 'succeeded'
    | 'failed'
    | 'refunded'
    | 'partially_refunded'
    | 'void'
    | 'authorized'
    | 'canceled'
  amountCents: number
  capturedCents: number | null
  capturedAt: string | null
  reference: string | null
  note: string | null
  /** Set when a linked customer's saved card paid (t1117). */
  cardOwnerName: string | null
  createdAt: string
}

const PAYMENT_STATUS_TONE: Record<
  RentalPayment['status'],
  'neutral' | 'warning' | 'danger' | 'info'
> = {
  pending: 'warning',
  succeeded: 'neutral',
  failed: 'danger',
  refunded: 'info',
  partially_refunded: 'info',
  void: 'neutral',
  authorized: 'info',
  canceled: 'neutral',
}
const PAYMENT_STATUS_LABEL: Record<RentalPayment['status'], string> = {
  pending: 'Pending',
  succeeded: '',
  failed: 'Failed',
  refunded: 'Refunded',
  partially_refunded: 'Partially refunded',
  void: 'Void',
  authorized: 'Hold',
  canceled: 'Hold released',
}

const PROVIDER_LABEL: Record<string, string> = {
  cash: 'Cash',
  external_card: 'Card machine',
  stripe: 'Stripe',
  check: 'Check',
  zelle: 'Zelle',
  deposit: 'From deposit',
}

interface RentalItem {
  id: string
  serializedUnitId: string
  imei: string
  serialNumber: string | null
  productId: string
  productName: string
  productType: string
  addedAt: string
  releasedAt: string | null
  verifiedAt: string | null
  productPriceCents: number
}

interface RentalDetail {
  id: string
  rentalNumber: string
  status: string
  customerId: string
  customerName: string | null
  customerPhone: string | null
  imei: string
  productName: string
  productType: string
  itemCount: number
  startDate: string
  dueDate: string | null
  returnedAt: string | null
  cancelledAt: string | null
  notReturnedAt: string | null
  feeCents: number
  billingInterval: string
  feePeriods: number
  feeTotalCents: number
  depositCents: number
  depositReturnedAt: string | null
  depositKeptAt: string | null
  returnAnnouncedAt: string | null
  returnReceivedAt: string | null
  needsAttentionAt: string | null
  needsAttentionReason: string | null
  notes: string | null
  payments: RentalPayment[]
  items: RentalItem[]
  /** True only for a cancelled rental with no payments and no history beyond
   *  its creation and cancellation — eligible for permanent deletion. */
  deletable: boolean
  /** Set when this rental was opened alongside a purchase in the same
   *  counter checkout (t0943) — null for a rental created on its own. */
  orderId: string | null
  orderNumber: string | null
  /** How much of the held deposit has been deducted for damage, and how much
   *  is still available to deduct or refund (t0944) — zero once the deposit
   *  is resolved (returned, kept, or fully captured off a hold). */
  depositAppliedCents: number
  depositAvailableCents: number
  createdAt: string
  updatedAt: string
}

interface RentalEvent {
  id: string
  fromStatus: string | null
  toStatus: string
  actorType: string
  actorLabel: string | null
  note: string | null
  createdAt: string
}

const ACTION_LABEL: Record<string, string> = {
  active: 'Reopen (mark active)',
  overdue: 'Mark overdue',
}

/** Statuses a rental is closed out into — each has its own dialog. */
const CLOSING_STATUSES = ['returned', 'cancelled', 'not_returned']

const PAYMENT_KIND_LABEL: Record<string, string> = {
  fee: 'Fee',
  deposit: 'Deposit',
  deposit_refund: 'Deposit refund',
  damage_fee: 'Damage fee',
}

/**
 * One rental: the customer, the unit, its terms, and its status history.
 * Same shape as the repair detail page — legal moves come from the server,
 * not a list kept here. Returning is its own primary action rather than a
 * generic transition, since it also decides what happens to the unit.
 */
export function RentalDetailPage() {
  const { rentalId } = useParams({ from: '/authed/rentals/$rentalId' })
  const navigate = useNavigate()
  const { can } = useAuth()
  const [moving, setMoving] = useState<string | null>(null)
  const [returning, setReturning] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [notReturning, setNotReturning] = useState(false)
  const [reopening, setReopening] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [addingItems, setAddingItems] = useState(false)
  const [recordingPayment, setRecordingPayment] = useState(false)
  const [recordingDamage, setRecordingDamage] = useState(false)
  const [editingDetails, setEditingDetails] = useState(false)
  const [voidingPayment, setVoidingPayment] = useState<RentalPayment | null>(null)
  const [deletingPayment, setDeletingPayment] = useState<RentalPayment | null>(null)
  const [releasingHold, setReleasingHold] = useState<RentalPayment | null>(null)
  const [capturingHold, setCapturingHold] = useState<RentalPayment | null>(null)

  const { data: rental, isLoading } = useQuery({
    queryKey: queryKeys.rentals.detail(rentalId),
    queryFn: () => api.get<RentalDetail>(`/rentals/${rentalId}`),
  })

  const { data: transitions } = useQuery({
    queryKey: queryKeys.rentals.transitions(rentalId),
    queryFn: () => api.get<{ from: string; to: string[] }>(`/rentals/${rentalId}/transitions`),
    enabled: Boolean(rental),
  })

  const { data: events } = useQuery({
    queryKey: queryKeys.rentals.events(rentalId),
    queryFn: () => api.get<RentalEvent[]>(`/rentals/${rentalId}/events`),
    enabled: Boolean(rental),
  })

  if (isLoading || !rental) {
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Page>
    )
  }

  // What the fee comes to (rate x days/weeks) less the fee already collected.
  const feeOwedCents = Math.max(
    0,
    rental.feeTotalCents -
      rental.payments
        .filter((p) => p.kind === 'fee' && p.status === 'succeeded')
        .reduce((sum, p) => sum + p.amountCents, 0),
  )

  const canWrite = can('rentals:write')
  // "returned", "cancelled" and "not_returned" are each offered through their
  // own dialog below, since closing a rental also decides what happens to its
  // units and its deposit.
  const moves = canWrite
    ? (transitions?.to ?? []).filter((to) => !CLOSING_STATUSES.includes(to))
    : []
  const canReturn = canWrite && (transitions?.to ?? []).includes('returned')
  const canCancel = canWrite && (transitions?.to ?? []).includes('cancelled')
  const canMarkNotReturned = canWrite && (transitions?.to ?? []).includes('not_returned')
  const isOpen = !CLOSING_STATUSES.includes(rental.status)

  const isActiveDeposit = (p: RentalPayment) =>
    p.kind === 'deposit' && ['pending', 'authorized', 'succeeded'].includes(p.status)
  const activeHold = rental.payments.find(
    (p) => p.kind === 'deposit' && p.provider === 'stripe' && p.status === 'authorized',
  )
  const showHoldRecoveryBanner =
    isOpen &&
    rental.depositCents > 0 &&
    !rental.depositReturnedAt &&
    !rental.depositKeptAt &&
    !rental.payments.some(isActiveDeposit) &&
    rental.payments.some(
      (p) => p.kind === 'deposit' && p.provider === 'stripe' && p.status === 'failed',
    )

  return (
    <Page>
      <div className="pb-3">
        <Link
          to="/rentals"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" /> All rentals
        </Link>
      </div>

      <PageHeader
        title={rental.rentalNumber}
        description={`${rental.customerName ?? 'Customer'}${
          rental.customerPhone ? ` · ${formatUsPhone(rental.customerPhone)}` : ''
        } · started ${new Date(rental.startDate).toLocaleDateString('en-US', { dateStyle: 'medium' })}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MarkImportantButton itemType="rental" itemId={rentalId} />
            <Badge tone={RENTAL_STATUS_TONE[rental.status] ?? 'neutral'}>
              {humanizeStatus(rental.status)}
            </Badge>
            {moves.map((to) => (
              <Button key={to} size="sm" variant="secondary" onClick={() => setMoving(to)}>
                {ACTION_LABEL[to] ?? humanizeStatus(to)}
              </Button>
            ))}
            {canCancel ? (
              <Button size="sm" variant="danger" onClick={() => setCancelling(true)}>
                Cancel rental
              </Button>
            ) : null}
            {canMarkNotReturned ? (
              <Button size="sm" variant="danger" onClick={() => setNotReturning(true)}>
                Not returned
              </Button>
            ) : null}
            {canReturn ? (
              <Button size="sm" variant="primary" onClick={() => setReturning(true)}>
                Return units
              </Button>
            ) : null}
            {canWrite && rental.status === 'returned' ? (
              <Button size="sm" variant="secondary" onClick={() => setReopening(true)}>
                Reopen rental
              </Button>
            ) : null}
            {canWrite && rental.deletable ? (
              <Button size="sm" variant="danger" onClick={() => setDeleting(true)}>
                Delete rental
              </Button>
            ) : null}
          </div>
        }
      />

      {rental.status === 'returned' ? (
        <Alert tone="success" className="mb-4">
          This rental is closed. Every unit is back in inventory.
          {canWrite ? ' Marked returned by mistake? Use Reopen rental.' : ''}
        </Alert>
      ) : rental.status === 'cancelled' ? (
        <Alert tone="info" className="mb-4">
          This rental was cancelled. Every unit is back in inventory.
          {rental.depositCents > 0
            ? rental.depositReturnedAt
              ? ' The deposit was refunded.'
              : rental.depositKeptAt
                ? ' The deposit was kept.'
                : rental.depositAppliedCents > 0
                  ? ' The deposit was fully applied to damage fees.'
                  : ''
            : ''}
        </Alert>
      ) : rental.status === 'not_returned' ? (
        <Alert tone="danger" className="mb-4">
          This rental was marked not returned. The unit was taken out of stock and shows as lost.
          {rental.depositCents > 0
            ? rental.depositReturnedAt
              ? ' The deposit was refunded.'
              : rental.depositKeptAt
                ? ' The deposit was kept.'
                : rental.depositAppliedCents > 0
                  ? ' The deposit was fully applied to the charge.'
                  : ''
            : ''}
        </Alert>
      ) : rental.status === 'overdue' ? (
        <Alert tone="danger" className="mb-4">
          Past due — not everything has been returned.
        </Alert>
      ) : null}

      {rental.needsAttentionAt ? (
        <AttentionBanner
          rentalId={rental.id}
          reason={rental.needsAttentionReason}
          canWrite={canWrite}
        />
      ) : null}

      {showHoldRecoveryBanner ? (
        <Alert tone="warning" className="mb-4">
          The card hold for the deposit expired or failed — collect the deposit again from Record
          payment.
        </Alert>
      ) : null}

      {rental.orderId && rental.orderNumber ? (
        <p className="mb-4 text-sm text-foreground-muted">
          Opened with order{' '}
          <Link
            to="/orders/$orderId"
            params={{ orderId: rental.orderId }}
            className="font-medium text-primary hover:underline"
          >
            {rental.orderNumber}
          </Link>
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          {isOpen ? <ReturnVerificationCard rental={rental} canWrite={canWrite} /> : null}

          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Package className="size-4" aria-hidden="true" /> Items (
                {rental.items.some((i) => i.releasedAt) && rental.status !== 'returned'
                  ? `${rental.items.filter((i) => !i.releasedAt).length} out of ${rental.items.length}`
                  : rental.items.length}
                )
              </CardTitle>
              <div className="flex items-center gap-2">
                {canWrite && isOpen ? (
                  <Button size="sm" variant="secondary" onClick={() => setAddingItems(true)}>
                    <Plus className="size-3.5" aria-hidden="true" /> Add items
                  </Button>
                ) : null}
                {canWrite ? (
                  <Button size="sm" variant="secondary" onClick={() => setEditingDetails(true)}>
                    <Pencil className="size-3.5" aria-hidden="true" /> Edit
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <ul className="divide-y divide-border">
                {rental.items.map((item) => {
                  const itemKind = unitIdentifierKind(item.productType as never)
                  return (
                    <li key={item.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{item.productName}</p>
                        <p className="font-identifier text-2xs text-foreground-muted">
                          {UNIT_IDENTIFIER_LABELS[itemKind]} {item.imei}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {item.verifiedAt ? <Badge tone="success">Verified</Badge> : null}
                        {item.releasedAt ? (
                          <Badge tone="neutral">
                            {rental.status === 'cancelled' || rental.status === 'not_returned'
                              ? 'Released'
                              : 'Returned'}
                          </Badge>
                        ) : (
                          <Badge tone="primary">Out</Badge>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Fee</p>
                <p>
                  {formatMoney(rental.feeCents)}
                  {rentalIntervalUnit(rental.billingInterval)
                    ? ` / ${rentalIntervalUnit(rental.billingInterval)}`
                    : ''}
                </p>
                {rental.feePeriods > 1 ? (
                  <p className="text-xs text-foreground-muted">
                    × {rental.feePeriods} {rentalIntervalUnit(rental.billingInterval)}s ={' '}
                    {formatMoney(rental.feeTotalCents)} total
                    {feeOwedCents > 0 ? ` · ${formatMoney(feeOwedCents)} still owed` : ''}
                  </p>
                ) : null}
              </div>
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Deposit</p>
                <p>
                  {rental.depositCents > 0 ? formatMoney(rental.depositCents) : 'None'}
                  {rental.depositAppliedCents > 0
                    ? ` · ${formatMoney(rental.depositAppliedCents)} applied to damage`
                    : ''}
                  {rental.depositReturnedAt ? ' · returned' : ''}
                  {rental.depositKeptAt ? ' · kept' : ''}
                  {isOpen && rental.depositAppliedCents > 0 && rental.depositAvailableCents > 0
                    ? ` · ${formatMoney(rental.depositAvailableCents)} remaining to refund`
                    : ''}
                </p>
              </div>
              {rental.notes ? (
                <div>
                  <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Notes</p>
                  <p>{rental.notes}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle>Payments</CardTitle>
              <div className="flex items-center gap-2">
                {canWrite && isOpen ? (
                  <Button size="sm" variant="secondary" onClick={() => setRecordingDamage(true)}>
                    Damage fee
                  </Button>
                ) : null}
                {canWrite ? (
                  <Button size="sm" variant="secondary" onClick={() => setRecordingPayment(true)}>
                    Record payment
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent>
              {rental.payments.length === 0 ? (
                <p className="text-sm text-foreground-muted">Nothing recorded yet.</p>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {rental.payments.map((payment) => {
                    const isHold = payment.kind === 'deposit' && payment.provider === 'stripe'
                    const isPartialCapture =
                      payment.status === 'succeeded' &&
                      payment.capturedCents != null &&
                      payment.capturedCents < payment.amountCents
                    return (
                      <li key={payment.id} className="flex items-center justify-between py-2">
                        <div>
                          <p className="flex items-center gap-2 font-medium">
                            {PAYMENT_KIND_LABEL[payment.kind] ?? payment.kind}
                            {payment.status !== 'succeeded' ? (
                              <Badge tone={PAYMENT_STATUS_TONE[payment.status]}>
                                {PAYMENT_STATUS_LABEL[payment.status]}
                              </Badge>
                            ) : null}
                          </p>
                          <p className="text-2xs text-foreground-muted">
                            {PROVIDER_LABEL[payment.provider] ?? payment.provider} ·{' '}
                            {new Date(payment.createdAt).toLocaleDateString('en-US', {
                              dateStyle: 'medium',
                            })}
                          </p>
                          {payment.cardOwnerName ? (
                            <p className="text-2xs text-foreground-muted">
                              Card of {payment.cardOwnerName}
                            </p>
                          ) : null}
                          {isPartialCapture ? (
                            <p className="text-2xs text-foreground-muted">
                              Captured {formatMoney(payment.capturedCents ?? 0)} of{' '}
                              {formatMoney(payment.amountCents)} hold — remainder released
                            </p>
                          ) : payment.note ? (
                            <p className="text-2xs text-foreground-muted">{payment.note}</p>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={
                              payment.status === 'void' || payment.status === 'canceled'
                                ? 'tabular font-medium text-foreground-muted line-through'
                                : 'tabular font-medium'
                            }
                          >
                            {formatMoney(
                              isPartialCapture ? (payment.capturedCents ?? 0) : payment.amountCents,
                            )}
                          </span>
                          {canWrite && payment.status === 'authorized' && isHold ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="More actions for this deposit hold"
                                >
                                  <MoreVertical className="size-3.5" aria-hidden="true" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => setReleasingHold(payment)}>
                                  Release hold
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setCapturingHold(payment)}>
                                  Capture hold…
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : canWrite && payment.kind !== 'deposit_refund' && !isHold ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="More actions for this payment"
                                >
                                  <MoreVertical className="size-3.5" aria-hidden="true" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {payment.status !== 'void' ? (
                                  <DropdownMenuItem onSelect={() => setVoidingPayment(payment)}>
                                    <Ban aria-hidden="true" /> Void payment
                                  </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuItem
                                  destructive
                                  onSelect={() => setDeletingPayment(payment)}
                                >
                                  <Trash2 aria-hidden="true" /> Delete payment
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {(events ?? []).map((event) => (
                  <li key={event.id} className="flex gap-3 text-sm">
                    <span
                      className="mt-1.5 size-1.5 shrink-0 rounded-full bg-border-strong"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p>
                        {event.fromStatus ? (
                          <>
                            <span className="text-foreground-muted">
                              {humanizeStatus(event.fromStatus)}
                            </span>{' '}
                            →{' '}
                          </>
                        ) : null}
                        <span className="font-medium">{humanizeStatus(event.toStatus)}</span>
                      </p>
                      <p className="text-2xs text-foreground-muted">
                        {event.actorLabel ?? event.actorType} ·{' '}
                        {new Date(event.createdAt).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                      {event.note ? (
                        <p className="mt-0.5 text-xs text-foreground-muted">{event.note}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <TermsCard rental={rental} />
          <Card>
            <CardHeader>
              <CardTitle>Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <Link
                to="/customers/$customerId"
                params={{ customerId: rental.customerId }}
                className="font-medium underline"
              >
                {rental.customerName ?? 'View customer'}
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      <TransitionDialog rental={rental} to={moving} onClose={() => setMoving(null)} />
      <ReturnDialog
        rental={rental}
        activeHold={activeHold ?? null}
        open={returning}
        onClose={() => setReturning(false)}
      />
      <ReopenRentalDialog rental={rental} open={reopening} onClose={() => setReopening(false)} />
      <CancelRentalDialog
        rental={rental}
        activeHold={activeHold ?? null}
        open={cancelling}
        onClose={() => setCancelling(false)}
      />
      <NotReturnedDialog
        rental={rental}
        activeHold={activeHold ?? null}
        open={notReturning}
        onClose={() => setNotReturning(false)}
      />
      <DeleteRentalDialog
        rental={rental}
        open={deleting}
        onClose={() => setDeleting(false)}
        onDeleted={() => navigate({ to: '/rentals' })}
      />
      <AddItemsDialog rental={rental} open={addingItems} onClose={() => setAddingItems(false)} />
      <RecordPaymentDialog
        rental={rental}
        open={recordingPayment}
        onClose={() => setRecordingPayment(false)}
      />
      <DamageFeeDialog
        rental={rental}
        activeHold={activeHold ?? null}
        open={recordingDamage}
        onClose={() => setRecordingDamage(false)}
      />
      <VoidPaymentDialog
        rental={rental}
        payment={voidingPayment}
        onClose={() => setVoidingPayment(null)}
      />
      <DeletePaymentDialog
        rental={rental}
        payment={deletingPayment}
        onClose={() => setDeletingPayment(null)}
      />
      <ReleaseHoldDialog
        rental={rental}
        payment={releasingHold}
        onClose={() => setReleasingHold(null)}
      />
      <CaptureHoldDialog
        rental={rental}
        payment={capturingHold}
        onClose={() => setCapturingHold(null)}
      />
      <EditDetailsDialog
        rental={rental}
        open={editingDetails}
        onClose={() => setEditingDetails(false)}
      />
    </Page>
  )
}

function TermsCard({ rental }: { rental: RentalDetail }) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canWrite = can('rentals:write')
  const [dueDate, setDueDate] = useState(rental.dueDate ?? '')

  useEffect(() => {
    setDueDate(rental.dueDate ?? '')
  }, [rental.dueDate])

  const dirty = dueDate !== (rental.dueDate ?? '')

  const save = useMutation({
    mutationFn: () => api.patch(`/rentals/${rental.id}`, { dueDate: dueDate || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      toast.success('Due date updated')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Due date</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label="Due" hint="Leave blank for an open-ended rental.">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              disabled={!canWrite || CLOSING_STATUSES.includes(rental.status)}
            />
          )}
        </Field>
        {canWrite && !CLOSING_STATUSES.includes(rental.status) ? (
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

function TransitionDialog({
  rental,
  to,
  onClose,
}: {
  rental: RentalDetail
  to: string | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')

  const move = useMutation({
    mutationFn: () =>
      api.post(`/rentals/${rental.id}/transition`, { to, note: note.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      toast.success(`${rental.rentalNumber} is now ${humanizeStatus(to ?? '').toLowerCase()}`)
      setNote('')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not update the rental',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={to !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {to ? (ACTION_LABEL[to] ?? humanizeStatus(to)) : ''} — {rental.rentalNumber}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Note" hint="Kept on the rental history.">
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => move.mutate()} disabled={move.isPending}>
            {move.isPending ? 'Working…' : to ? (ACTION_LABEL[to] ?? humanizeStatus(to)) : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReturnDialog({
  rental,
  activeHold,
  open,
  onClose,
}: {
  rental: RentalDetail
  activeHold: RentalPayment | null
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [dispositionByItemId, setDispositionByItemId] = useState<
    Record<string, 'sellable' | 'provisioning'>
  >({})
  const [depositReturned, setDepositReturned] = useState(false)
  const [depositProvider, setDepositProvider] = useState('cash')
  const [holdAction, setHoldAction] = useState<'release' | 'capture'>('release')
  const [captureAmount, setCaptureAmount] = useState('')
  const [note, setNote] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const openItems = rental.items.filter((i) => !i.releasedAt)
  // Ticking only some of the items is a partial return (t1259): the rental
  // stays open, and the deposit and any card hold are left for the final return.
  const partial = selectedIds.size > 0 && selectedIds.size < openItems.length
  // A hold in place takes over the deposit section entirely — the manual
  // refund-record UI is only for a deposit that was never a card hold, or one
  // already captured/released before this feature (t0951). `depositAvailableCents`
  // also accounts for whatever a damage fee already deducted (t0944).
  const hasDeposit = !activeHold && rental.depositAvailableCents > 0

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset once, on open
  useEffect(() => {
    if (!open) return
    setDispositionByItemId(Object.fromEntries(openItems.map((i) => [i.id, 'sellable' as const])))
    setSelectedIds(new Set(openItems.map((i) => i.id)))
    setHoldAction('release')
    setCaptureAmount(activeHold ? (activeHold.amountCents / 100).toFixed(2) : '')
  }, [open])

  const captureAmountCents = Math.round(Number(captureAmount) * 100)
  const validCaptureAmount =
    activeHold != null &&
    captureAmountCents > 0 &&
    captureAmountCents <= activeHold.amountCents &&
    !Number.isNaN(captureAmountCents)

  const submit = useMutation({
    mutationFn: async () => {
      if (partial) {
        return api.post(`/rentals/${rental.id}/return-items`, {
          items: openItems
            .filter((i) => selectedIds.has(i.id))
            .map((i) => ({ itemId: i.id, disposition: dispositionByItemId[i.id] ?? 'sellable' })),
          note: note.trim() || null,
        })
      }
      if (activeHold) {
        if (holdAction === 'release') {
          await api.post(`/rentals/${rental.id}/payments/${activeHold.id}/release-hold`, {})
        } else {
          await api.post(`/rentals/${rental.id}/payments/${activeHold.id}/capture-hold`, {
            amountCents: captureAmountCents,
          })
        }
      }
      return api.post(`/rentals/${rental.id}/return`, {
        items: openItems.map((i) => ({
          itemId: i.id,
          disposition: dispositionByItemId[i.id] ?? 'sellable',
        })),
        depositReturned: activeHold ? false : hasDeposit ? depositReturned : false,
        depositRefund:
          !activeHold && hasDeposit && depositReturned
            ? { provider: depositProvider, amountCents: rental.depositAvailableCents }
            : null,
        note: note.trim() || null,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success(
        partial
          ? `${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'} returned — ${rental.rentalNumber} stays open`
          : `${rental.rentalNumber} returned`,
      )
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not return the rental',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const canSubmit =
    !submit.isPending &&
    selectedIds.size > 0 &&
    (partial || !activeHold || holdAction === 'release' || validCaptureAmount)

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Return {rental.rentalNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {openItems.length > 1 ? (
            <p className="text-sm text-foreground-muted">
              Untick any item that is still out to return only the rest.
            </p>
          ) : null}
          {openItems.map((item) => (
            <div key={item.id} className="space-y-1">
              {openItems.length > 1 ? (
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={(e) =>
                      setSelectedIds((prev) => {
                        const next = new Set(prev)
                        if (e.target.checked) next.add(item.id)
                        else next.delete(item.id)
                        return next
                      })
                    }
                    className="size-4 accent-primary"
                  />
                  <span>Returning {item.productName}</span>
                </label>
              ) : null}
              <Field label={`${item.productName} — where it goes`}>
                {(props) => (
                  <Select
                    value={dispositionByItemId[item.id] ?? 'sellable'}
                    onValueChange={(v) =>
                      setDispositionByItemId((prev) => ({
                        ...prev,
                        [item.id]: v as 'sellable' | 'provisioning',
                      }))
                    }
                  >
                    <SelectTrigger {...props}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sellable">
                        Back on the shelf — ready to sell or rent again
                      </SelectItem>
                      <SelectItem value="provisioning">Needs a recheck or wipe first</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          ))}

          {partial ? (
            <p className="text-sm text-foreground-muted">
              {rental.rentalNumber} stays open for the {openItems.length - selectedIds.size} other
              item{openItems.length - selectedIds.size === 1 ? '' : 's'}. The rental fee and deposit
              don't change; if something isn't coming back, charge a damage fee from the rental
              page.
            </p>
          ) : activeHold ? (
            <div className="space-y-2">
              <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                Deposit hold — {formatMoney(activeHold.amountCents)}
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="return-hold"
                  checked={holdAction === 'release'}
                  onChange={() => setHoldAction('release')}
                  className="size-4 accent-primary"
                />
                <span>Release the hold — nothing is charged</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="return-hold"
                  checked={holdAction === 'capture'}
                  onChange={() => setHoldAction('capture')}
                  className="size-4 accent-primary"
                />
                <span>Keep some or all — capture from the card</span>
              </label>
              {holdAction === 'capture' ? (
                <Field
                  label="Amount to capture"
                  hint="Anything less than the full hold is released back to the card."
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={0}
                      step="0.01"
                      value={captureAmount}
                      onChange={(e) => setCaptureAmount(e.target.value)}
                    />
                  )}
                </Field>
              ) : null}
            </div>
          ) : hasDeposit ? (
            <>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={depositReturned}
                  onChange={(e) => setDepositReturned(e.target.checked)}
                  className="size-4 accent-primary"
                />
                <span>Returning the {formatMoney(rental.depositAvailableCents)} deposit now</span>
              </label>
              {depositReturned ? (
                <Field label="How">
                  {(props) => (
                    <Select value={depositProvider} onValueChange={setDepositProvider}>
                      <SelectTrigger {...props}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="external_card">Card machine</SelectItem>
                        <SelectItem value="stripe">Stripe</SelectItem>
                        <SelectItem value="check">Check</SelectItem>
                        <SelectItem value="zelle">Zelle</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              ) : null}
            </>
          ) : null}

          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => submit.mutate()} disabled={!canSubmit}>
            {submit.isPending
              ? 'Working…'
              : partial
                ? `Return ${selectedIds.size} of ${openItems.length} items`
                : 'Return units'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Cancels the rental outright: every attached unit goes straight back to
 * `sellable` (no disposition choice, unlike a return), and staff record
 * whether the deposit was refunded or kept.
 */
function ReopenRentalDialog({
  rental,
  open,
  onClose,
}: {
  rental: RentalDetail
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')

  useEffect(() => {
    if (open) setNote('')
  }, [open])

  const submit = useMutation({
    mutationFn: () => api.post(`/rentals/${rental.id}/reopen`, { note: note.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success(`${rental.rentalNumber} reopened`)
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not reopen the rental',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reopen {rental.rentalNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Alert tone="info">
            Undoes the return: the rental goes back to active (or overdue if it is past its due
            date) and the units that came back go out on rental again. This is refused if a unit has
            since been sold or rented to someone else.
          </Alert>
          {rental.depositReturnedAt || rental.depositKeptAt ? (
            <Alert tone="warning">
              The deposit was already settled when the rental was returned. It is left as recorded —
              record a new deposit if you take one again.
            </Alert>
          ) : null}
          <Field label="Reason (optional)" hint="Kept on the rental history.">
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={submit.isPending} onClick={() => submit.mutate()}>
            Reopen rental
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CancelRentalDialog({
  rental,
  activeHold,
  open,
  onClose,
}: {
  rental: RentalDetail
  activeHold: RentalPayment | null
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [deposit, setDeposit] = useState<'refund' | 'keep'>('keep')
  const [depositProvider, setDepositProvider] = useState('cash')
  const [holdAction, setHoldAction] = useState<'release' | 'capture'>('release')
  const [note, setNote] = useState('')

  const hasDeposit = !activeHold && rental.depositAvailableCents > 0
  const openItemCount = rental.items.filter((i) => !i.releasedAt).length

  useEffect(() => {
    if (!open) return
    setDeposit('keep')
    setHoldAction('release')
    setNote('')
  }, [open])

  const submit = useMutation({
    mutationFn: async () => {
      if (activeHold) {
        if (holdAction === 'release') {
          await api.post(`/rentals/${rental.id}/payments/${activeHold.id}/release-hold`, {})
        } else {
          await api.post(`/rentals/${rental.id}/payments/${activeHold.id}/capture-hold`, {})
        }
      }
      return api.post(`/rentals/${rental.id}/cancel`, {
        deposit: activeHold ? 'keep' : hasDeposit ? deposit : 'keep',
        depositRefund:
          !activeHold && hasDeposit && deposit === 'refund'
            ? { provider: depositProvider, amountCents: rental.depositAvailableCents }
            : null,
        note: note.trim() || null,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success(`${rental.rentalNumber} cancelled`)
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not cancel the rental',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel {rental.rentalNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Alert tone="warning">
            Cancels {rental.rentalNumber} immediately —{' '}
            {openItemCount === 1 ? 'the unit goes' : `all ${openItemCount} units go`} back to
            available inventory.
          </Alert>

          {activeHold ? (
            <div className="space-y-2">
              <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                Deposit hold — {formatMoney(activeHold.amountCents)}
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="cancel-hold"
                  checked={holdAction === 'release'}
                  onChange={() => setHoldAction('release')}
                  className="size-4 accent-primary"
                />
                <span>Release the hold — nothing is charged</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="cancel-hold"
                  checked={holdAction === 'capture'}
                  onChange={() => setHoldAction('capture')}
                  className="size-4 accent-primary"
                />
                <span>Keep the full deposit — capture from the card</span>
              </label>
            </div>
          ) : hasDeposit ? (
            <div className="space-y-2">
              <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Deposit</p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="cancel-deposit"
                  checked={deposit === 'refund'}
                  onChange={() => setDeposit('refund')}
                  className="size-4 accent-primary"
                />
                <span>Refund the {formatMoney(rental.depositAvailableCents)} deposit</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="cancel-deposit"
                  checked={deposit === 'keep'}
                  onChange={() => setDeposit('keep')}
                  className="size-4 accent-primary"
                />
                <span>Keep the deposit</span>
              </label>
              {deposit === 'refund' ? (
                <Field label="How">
                  {(props) => (
                    <Select value={depositProvider} onValueChange={setDepositProvider}>
                      <SelectTrigger {...props}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="external_card">Card machine</SelectItem>
                        <SelectItem value="stripe">Stripe</SelectItem>
                        <SelectItem value="check">Check</SelectItem>
                        <SelectItem value="zelle">Zelle</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              ) : null}
            </div>
          ) : null}

          <Field label="Note" hint="Kept on the rental history.">
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Back
          </Button>
          <Button variant="danger" onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? 'Working…' : 'Cancel rental'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Marks a rental as never brought back (t1255), in two steps: first the
 * replacement charge — the damage-fee dialog, pre-filled with the shelf price
 * of whatever is still out and editable, or skipped — then the close-out,
 * which settles the deposit like a cancellation and writes the units off as
 * lost. The charge is recorded before the rental closes, so a failed close
 * can be retried without charging twice.
 */
function NotReturnedDialog({
  rental,
  activeHold,
  open,
  onClose,
}: {
  rental: RentalDetail
  activeHold: RentalPayment | null
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<'charge' | 'close'>('charge')
  const [charging, setCharging] = useState(false)
  const [deposit, setDeposit] = useState<'refund' | 'keep'>('keep')
  const [depositProvider, setDepositProvider] = useState('cash')
  const [holdAction, setHoldAction] = useState<'release' | 'capture'>('release')
  const [note, setNote] = useState('')

  const openItems = rental.items.filter((i) => !i.releasedAt)
  const suggestedCents = openItems.reduce((sum, i) => sum + i.productPriceCents, 0)
  const hasDeposit = !activeHold && rental.depositAvailableCents > 0

  useEffect(() => {
    if (!open) return
    setStep('charge')
    setCharging(false)
    setDeposit('keep')
    setHoldAction('release')
    setNote('')
  }, [open])

  const submit = useMutation({
    mutationFn: async () => {
      if (activeHold) {
        if (holdAction === 'release') {
          await api.post(`/rentals/${rental.id}/payments/${activeHold.id}/release-hold`, {})
        } else {
          await api.post(`/rentals/${rental.id}/payments/${activeHold.id}/capture-hold`, {})
        }
      }
      return api.post(`/rentals/${rental.id}/not-returned`, {
        deposit: activeHold ? 'keep' : hasDeposit ? deposit : 'keep',
        depositRefund:
          !activeHold && hasDeposit && deposit === 'refund'
            ? { provider: depositProvider, amountCents: rental.depositAvailableCents }
            : null,
        note: note.trim() || null,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success(`${rental.rentalNumber} marked not returned`)
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not mark the rental not returned',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <>
      <Dialog open={open && !charging} onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Not returned — {rental.rentalNumber}</DialogTitle>
          </DialogHeader>
          {step === 'charge' ? (
            <>
              <DialogBody className="space-y-4">
                <Alert tone="warning">
                  Use this when the customer never brings{' '}
                  {openItems.length === 1 ? 'the unit' : 'the units'} back. The customer is charged,
                  the rental closes as not returned, and the{' '}
                  {openItems.length === 1 ? 'unit is' : 'units are'} taken out of stock.
                </Alert>
                <ul className="divide-y divide-border text-sm">
                  {openItems.map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-3 py-2">
                      <span className="truncate">{item.productName}</span>
                      <span className="tabular">{formatMoney(item.productPriceCents)}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-foreground-muted">
                  The charge starts at the current price, {formatMoney(suggestedCents)}, and you can
                  change the amount before collecting it.
                </p>
              </DialogBody>
              <DialogFooter>
                <Button variant="ghost" onClick={onClose}>
                  Back
                </Button>
                <Button variant="secondary" onClick={() => setStep('close')}>
                  No charge
                </Button>
                <Button variant="danger" onClick={() => setCharging(true)}>
                  Charge customer…
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogBody className="space-y-4">
                {activeHold ? (
                  <div className="space-y-2">
                    <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                      Deposit hold — {formatMoney(activeHold.amountCents)}
                    </p>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="notreturned-hold"
                        checked={holdAction === 'release'}
                        onChange={() => setHoldAction('release')}
                        className="size-4 accent-primary"
                      />
                      <span>Release the hold — nothing is charged</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="notreturned-hold"
                        checked={holdAction === 'capture'}
                        onChange={() => setHoldAction('capture')}
                        className="size-4 accent-primary"
                      />
                      <span>Keep the full deposit — capture from the card</span>
                    </label>
                  </div>
                ) : hasDeposit ? (
                  <div className="space-y-2">
                    <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                      Deposit
                    </p>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="notreturned-deposit"
                        checked={deposit === 'refund'}
                        onChange={() => setDeposit('refund')}
                        className="size-4 accent-primary"
                      />
                      <span>Refund the {formatMoney(rental.depositAvailableCents)} deposit</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="notreturned-deposit"
                        checked={deposit === 'keep'}
                        onChange={() => setDeposit('keep')}
                        className="size-4 accent-primary"
                      />
                      <span>Keep the deposit</span>
                    </label>
                    {deposit === 'refund' ? (
                      <Field label="How">
                        {(props) => (
                          <Select value={depositProvider} onValueChange={setDepositProvider}>
                            <SelectTrigger {...props}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash">Cash</SelectItem>
                              <SelectItem value="external_card">Card machine</SelectItem>
                              <SelectItem value="stripe">Stripe</SelectItem>
                              <SelectItem value="check">Check</SelectItem>
                              <SelectItem value="zelle">Zelle</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </Field>
                    ) : null}
                  </div>
                ) : null}

                <Field label="Note" hint="Kept on the rental history.">
                  {(props) => (
                    <Textarea
                      {...props}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                    />
                  )}
                </Field>
              </DialogBody>
              <DialogFooter>
                <Button variant="ghost" onClick={onClose}>
                  Back
                </Button>
                <Button
                  variant="danger"
                  onClick={() => submit.mutate()}
                  disabled={submit.isPending}
                >
                  {submit.isPending ? 'Working…' : 'Mark not returned'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <DamageFeeDialog
        rental={rental}
        activeHold={activeHold}
        open={open && charging}
        onClose={() => setCharging(false)}
        title="Replacement charge"
        initialAmountCents={suggestedCents}
        initialReason="Not returned — replacement charge"
        onCharged={() => setStep('close')}
      />
    </>
  )
}

type DamageChargeMethod =
  | 'cash'
  | 'external_card'
  | 'check'
  | 'zelle'
  | 'new_card'
  | 'saved_card'
  | 'reader'

/**
 * A damage fee against an open rental (t0944) — a reason is always required.
 * Staff choose to deduct from whatever is still available on the deposit
 * (capped there — the remainder, if any, is collected as a new charge below)
 * or to leave the deposit untouched and charge the full amount outright.
 *
 * A manual remainder (cash, card machine, check, zelle) rides the same
 * `/damage-fee` call that applies the deduction — the server does the
 * splitting. An integrated card charge (a new card, a saved card, a reader)
 * can't ride that call, since only a real tender goes through the card
 * intent/saved-card endpoints — so when the remainder is card-collected, the
 * deposit-funded portion is applied first, on its own, and the dialog then
 * moves to the same card-collection step `RecordPaymentDialog` uses, kind
 * `damage_fee`, for whatever is left.
 */
function DamageFeeDialog({
  rental,
  activeHold,
  open,
  onClose,
  title,
  initialAmountCents,
  initialReason,
  onCharged,
}: {
  rental: RentalDetail
  activeHold: RentalPayment | null
  open: boolean
  onClose: () => void
  /** The not-returned flow (t1255) reuses this dialog for its replacement
   *  charge: a different title, a pre-filled amount and reason, and a hook that
   *  runs once the charge has settled. */
  title?: string
  initialAmountCents?: number
  initialReason?: string
  onCharged?: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [mode, setMode] = useState<'deposit' | 'charge'>('deposit')
  const [method, setMethod] = useState<DamageChargeMethod>('cash')
  const [phase, setPhase] = useState<'form' | 'collecting'>('form')
  const [collectAmountCents, setCollectAmountCents] = useState(0)
  const [intent, setIntent] = useState<RentalCardIntent | null>(null)
  const [readerReady, setReaderReady] = useState(false)

  // A hold caps the deduction at what's actually authorized on the card;
  // otherwise it's whatever `depositLedger` still has available.
  const depositCapCents = activeHold ? activeHold.amountCents : rental.depositAvailableCents
  const depositEligible = depositCapCents > 0

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset once, on open
  useEffect(() => {
    if (!open) return
    setAmount(initialAmountCents ? (initialAmountCents / 100).toFixed(2) : '')
    setReason(initialReason ?? '')
    setMode(depositEligible ? 'deposit' : 'charge')
    setMethod('cash')
    setPhase('form')
    setCollectAmountCents(0)
    setIntent(null)
    setReaderReady(false)
  }, [open])

  const amountCents = Math.round(Number(amount) * 100)
  const validAmount = amountCents > 0 && !Number.isNaN(amountCents)
  const validReason = reason.trim().length > 0
  const depositPortion = mode === 'deposit' ? Math.min(amountCents, depositCapCents) : 0
  const remainderPortion = Math.max(0, amountCents - depositPortion)
  const needsMethod = mode === 'charge' || remainderPortion > 0
  const isCardMethod = method === 'new_card' || method === 'saved_card' || method === 'reader'
  const canSubmitForm = validAmount && validReason

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })

  const abandon = () => {
    if (intent) {
      void api
        .post(`/rentals/${rental.id}/payments/${intent.rentalPaymentId}/cancel`, {})
        .catch(() => {})
    }
  }

  const close = () => {
    abandon()
    onClose()
  }

  const submitDamageFee = useMutation({
    mutationFn: (payload: { amountCents: number; mode: 'deposit' | 'charge'; provider?: string }) =>
      api.post(`/rentals/${rental.id}/damage-fee`, {
        amountCents: payload.amountCents,
        note: reason.trim(),
        mode: payload.mode,
        provider: payload.provider ?? null,
      }),
    onSuccess: async () => {
      await invalidate()
      toast.success(onCharged ? 'Charge recorded' : 'Damage fee recorded')
      onClose()
      onCharged?.()
    },
    onError: (error) =>
      toast.error(
        'Could not record the damage fee',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  // Applies the deposit-funded portion on its own, before a card charge
  // collects whatever is left — the two can't ride the same request.
  const startDepositLeg = useMutation({
    mutationFn: () =>
      api.post(`/rentals/${rental.id}/damage-fee`, {
        amountCents: depositPortion,
        note: reason.trim(),
        mode: 'deposit',
      }),
    onSuccess: async () => {
      await invalidate()
      setCollectAmountCents(remainderPortion)
      setPhase('collecting')
    },
    onError: (error) =>
      toast.error(
        'Could not apply the deposit',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const startIntent = useMutation({
    mutationFn: () =>
      api.post<RentalCardIntent>(`/rentals/${rental.id}/payments/card-intent`, {
        kind: 'damage_fee',
        amountCents: collectAmountCents,
        method: method === 'reader' ? 'reader' : 'card',
        note: reason.trim(),
      }),
    onSuccess: (result) => setIntent(result),
    onError: (error) =>
      toast.error(
        'Could not start the charge',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const onCardSettled = async () => {
    await invalidate()
    toast.success(onCharged ? 'Charge collected' : 'Damage fee charged')
    onClose()
    onCharged?.()
  }

  const handleSubmit = () => {
    if (!needsMethod) {
      submitDamageFee.mutate({ amountCents: depositPortion, mode: 'deposit' })
      return
    }
    if (isCardMethod) {
      if (depositPortion > 0) {
        startDepositLeg.mutate()
      } else {
        setCollectAmountCents(amountCents)
        setPhase('collecting')
      }
      return
    }
    submitDamageFee.mutate({ amountCents, mode, provider: method })
  }

  const showContinue = !intent && (method === 'new_card' || (method === 'reader' && readerReady))
  const working = submitDamageFee.isPending || startDepositLeg.isPending

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {title ?? 'Damage fee'} — {rental.rentalNumber}
          </DialogTitle>
        </DialogHeader>
        {phase === 'form' ? (
          <>
            <DialogBody className="space-y-4">
              <Field label="Amount" required>
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={0}
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Reason" required hint="Shown in the rental's payment history.">
                {(props) => (
                  <Textarea
                    {...props}
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                )}
              </Field>

              {depositEligible ? (
                <div className="space-y-2">
                  <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                    How is this applied?
                  </p>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="damage-mode"
                      checked={mode === 'deposit'}
                      onChange={() => setMode('deposit')}
                      className="size-4 accent-primary"
                    />
                    <span>
                      Deduct from the deposit ({formatMoney(depositCapCents)}
                      {activeHold ? ' held' : ' available'})
                    </span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="damage-mode"
                      checked={mode === 'charge'}
                      onChange={() => setMode('charge')}
                      className="size-4 accent-primary"
                    />
                    <span>New charge — leave the deposit untouched</span>
                  </label>
                  {mode === 'deposit' && validAmount ? (
                    <p className="text-sm text-foreground-muted">
                      {remainderPortion > 0
                        ? `Applies ${formatMoney(depositPortion)} from the deposit — collect the remaining ${formatMoney(remainderPortion)} below.`
                        : `Applies ${formatMoney(depositPortion)} from the deposit.${
                            depositCapCents - depositPortion > 0
                              ? ` ${formatMoney(depositCapCents - depositPortion)} will remain to refund.`
                              : ''
                          }`}
                    </p>
                  ) : null}
                  {mode === 'deposit' && activeHold ? (
                    <p className="text-2xs text-foreground-muted">
                      This deposit is a card hold — capturing less than the full hold releases the
                      rest back to the card automatically.
                    </p>
                  ) : null}
                </div>
              ) : null}

              {needsMethod ? (
                <Field label="How is the rest collected?">
                  {(props) => (
                    <Select
                      value={method}
                      onValueChange={(v) => setMethod(v as DamageChargeMethod)}
                    >
                      <SelectTrigger {...props}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="external_card">Card machine</SelectItem>
                        <SelectItem value="check">Check</SelectItem>
                        <SelectItem value="zelle">Zelle</SelectItem>
                        <SelectItem value="new_card">Charge card</SelectItem>
                        <SelectItem value="saved_card">Saved card</SelectItem>
                        <SelectItem value="reader">Card reader</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSubmit} disabled={!canSubmitForm || working}>
                {working ? 'Working…' : isCardMethod && needsMethod ? 'Continue' : 'Record'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogBody className="space-y-4">
              <p className="text-sm text-foreground-muted">
                Collecting {formatMoney(collectAmountCents)} for the damage fee.
              </p>

              {method === 'saved_card' ? (
                <SavedCardPanel
                  rentalId={rental.id}
                  customerId={rental.customerId}
                  kind="damage_fee"
                  amountCents={collectAmountCents}
                  note={reason.trim()}
                  validAmount={collectAmountCents > 0}
                  onSettled={onCardSettled}
                />
              ) : null}

              {method === 'new_card' && intent ? (
                <NewCardPanel
                  intent={intent}
                  rentalId={rental.id}
                  kind="damage_fee"
                  onSettled={onCardSettled}
                />
              ) : null}

              {method === 'reader' ? (
                <ReaderPanel
                  rentalId={rental.id}
                  intent={intent}
                  kind="damage_fee"
                  onReady={setReaderReady}
                  onSettled={onCardSettled}
                />
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              {showContinue ? (
                <Button
                  variant="primary"
                  disabled={startIntent.isPending}
                  loading={startIntent.isPending}
                  onClick={() => startIntent.mutate()}
                >
                  Continue
                </Button>
              ) : null}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface AddableUnitRow {
  id: string
  productId: string
  productName: string
  productType: string
  imei: string
  locationName: string | null
}

/** Attaches one or more available devices to an already-open rental (t0940).
 *  Same device search as `NewRentalDialog` in the rentals list, but
 *  multi-select. */
function AddItemsDialog({
  rental,
  open,
  onClose,
}: {
  rental: RentalDetail
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<AddableUnitRow[]>([])
  const [unitSearch, setUnitSearch] = useState('')

  useEffect(() => {
    if (!open) return
    setSelected([])
    setUnitSearch('')
  }, [open])

  const { data: units } = useQuery({
    queryKey: queryKeys.units.list({ q: unitSearch, status: 'sellable', unassigned: true }),
    queryFn: () =>
      api.get<{ items: AddableUnitRow[] }>('/units', {
        query: { q: unitSearch, status: 'sellable', unassigned: true, pageSize: 10 },
      }),
    enabled: unitSearch.trim().length >= 2,
  })

  const selectedIds = new Set(selected.map((u) => u.id))
  const results = (units?.items ?? []).filter((u) => !selectedIds.has(u.id))

  const submit = useMutation({
    mutationFn: () =>
      api.post(`/rentals/${rental.id}/items`, { unitIds: selected.map((u) => u.id) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success(selected.length === 1 ? 'Item added' : `${selected.length} items added`)
      onClose()
    },
    onError: (error) =>
      toast.error('Could not add the item', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add items to {rental.rentalNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field
            label="Devices"
            hint="Search by IMEI, ICCID, or product name — only sellable units show up."
          >
            {() => (
              <div className="space-y-2">
                <Input
                  value={unitSearch}
                  onChange={(e) => setUnitSearch(e.target.value)}
                  placeholder="IMEI, ICCID, or product…"
                />
                {results.length ? (
                  <ul className="divide-y divide-border rounded-lg border border-border">
                    {results.map((row) => (
                      <li key={row.id}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                          onClick={() => {
                            setSelected((prev) => [...prev, row])
                            setUnitSearch('')
                          }}
                        >
                          <span className="block font-medium">{row.productName}</span>
                          <span className="block font-identifier text-2xs text-foreground-muted">
                            {UNIT_IDENTIFIER_LABELS[unitIdentifierKind(row.productType as never)]}{' '}
                            {row.imei}
                            {row.locationName ? ` — ${row.locationName}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )}
          </Field>

          {selected.length ? (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {selected.map((row) => (
                <li key={row.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{row.productName}</p>
                    <p className="font-identifier text-2xs text-foreground-muted">
                      {UNIT_IDENTIFIER_LABELS[unitIdentifierKind(row.productType as never)]}{' '}
                      {row.imei}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelected((prev) => prev.filter((u) => u.id !== row.id))}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => submit.mutate()}
            disabled={selected.length === 0 || submit.isPending}
          >
            {submit.isPending ? 'Working…' : 'Add items'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditDetailsDialog({
  rental,
  open,
  onClose,
}: {
  rental: RentalDetail
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [feeCents, setFeeCents] = useState((rental.feeCents / 100).toFixed(2))
  const [billingInterval, setBillingInterval] = useState(rental.billingInterval)
  const [depositCents, setDepositCents] = useState((rental.depositCents / 100).toFixed(2))
  const [notes, setNotes] = useState(rental.notes ?? '')

  useEffect(() => {
    if (!open) return
    setFeeCents((rental.feeCents / 100).toFixed(2))
    setBillingInterval(rental.billingInterval)
    setDepositCents((rental.depositCents / 100).toFixed(2))
    setNotes(rental.notes ?? '')
  }, [open, rental.feeCents, rental.billingInterval, rental.depositCents, rental.notes])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/rentals/${rental.id}`, {
        feeCents: Math.round(Number(feeCents) * 100),
        billingInterval,
        depositCents: Math.round(Number(depositCents) * 100),
        notes: notes.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      toast.success('Rental updated')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const valid = Number(feeCents) >= 0 && Number(depositCents) >= 0

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {rental.rentalNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Billing">
            {(props) => (
              <Select value={billingInterval} onValueChange={setBillingInterval}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RENTAL_BILLING_INTERVALS.map((interval) => (
                    <SelectItem key={interval} value={interval}>
                      {rentalIntervalLabel(interval)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field
            label={
              rentalIntervalUnit(billingInterval)
                ? `Rate per ${rentalIntervalUnit(billingInterval)}`
                : 'Fee'
            }
            required
            hint={(() => {
              const periods = rentalFeePeriods({
                billingInterval,
                startDate: rental.startDate,
                dueDate: rental.dueDate,
              })
              if (periods <= 1) return undefined
              const rate = Math.round(Number(feeCents) * 100) || 0
              return `${formatMoney(rate)} × ${periods} = ${formatMoney(rate * periods)} total`
            })()}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                step="0.01"
                value={feeCents}
                onChange={(e) => setFeeCents(e.target.value)}
              />
            )}
          </Field>
          <Field label="Deposit">
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                step="0.01"
                value={depositCents}
                onChange={(e) => setDepositCents(e.target.value)}
              />
            )}
          </Field>
          <Field label="Notes">
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** For a payment the customer really made but that was later reversed — a
 *  bounced check, a changed mind, a chargeback. The entry stays visible,
 *  marked Void, rather than disappearing the way Delete does. */
function VoidPaymentDialog({
  rental,
  payment,
  onClose,
}: {
  rental: RentalDetail
  payment: RentalPayment | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const voidPayment = useMutation({
    mutationFn: () => api.post(`/rentals/${rental.id}/payments/${payment?.id}/void`, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      toast.success('Payment voided')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not void this payment',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={!!payment} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Void this payment?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-foreground-muted">
            {payment
              ? `The ${formatMoney(payment.amountCents)} ${(PAYMENT_KIND_LABEL[payment.kind] ?? payment.kind).toLowerCase()} entry`
              : 'This payment'}{' '}
            stays in the history, marked Void, and no longer counts as collected. Use this when the
            customer really paid but the payment was later reversed — a bounced check, a changed
            mind, or a chargeback.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => voidPayment.mutate()}
            disabled={voidPayment.isPending}
          >
            {voidPayment.isPending ? 'Voiding…' : 'Void payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Releases a card authorization hold on a deposit — the card was never
 *  charged. Used from the payment row's own menu, independent of Return or
 *  Cancel (which offer the same choice inline when a hold is active). */
function ReleaseHoldDialog({
  rental,
  payment,
  onClose,
}: {
  rental: RentalDetail
  payment: RentalPayment | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const release = useMutation({
    mutationFn: () => api.post(`/rentals/${rental.id}/payments/${payment?.id}/release-hold`, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      toast.success('Deposit hold released')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not release this hold',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={!!payment} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Release this deposit hold?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-foreground-muted">
            {payment
              ? `The ${formatMoney(payment.amountCents)} hold on the customer's card`
              : 'This hold'}{' '}
            is released — nothing is charged, and the card shows the reservation dropping off in a
            few days.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => release.mutate()} disabled={release.isPending}>
            {release.isPending ? 'Releasing…' : 'Release hold'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Captures some or all of an authorized deposit hold — Stripe releases
 *  whatever isn't captured back to the card automatically. */
function CaptureHoldDialog({
  rental,
  payment,
  onClose,
}: {
  rental: RentalDetail
  payment: RentalPayment | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')

  useEffect(() => {
    if (payment) setAmount((payment.amountCents / 100).toFixed(2))
  }, [payment])

  const amountCents = Math.round(Number(amount) * 100)
  const valid =
    payment != null &&
    amountCents > 0 &&
    amountCents <= payment.amountCents &&
    !Number.isNaN(amountCents)

  const capture = useMutation({
    mutationFn: () =>
      api.post(`/rentals/${rental.id}/payments/${payment?.id}/capture-hold`, { amountCents }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      toast.success('Deposit captured')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not capture this hold',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={!!payment} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Capture this deposit hold?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field
            label="Amount to capture"
            hint="Anything less than the full hold is released back to the card."
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => capture.mutate()}
            disabled={!valid || capture.isPending}
          >
            {capture.isPending ? 'Capturing…' : 'Capture'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** For an entry recorded by mistake — the customer never actually gave this
 *  payment, so nothing should remain in the history. Use Void instead when
 *  the payment genuinely happened and was only later reversed. */
function DeletePaymentDialog({
  rental,
  payment,
  onClose,
}: {
  rental: RentalDetail
  payment: RentalPayment | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const deletePayment = useMutation({
    mutationFn: () => api.delete(`/rentals/${rental.id}/payments/${payment?.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      toast.success('Payment deleted')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not delete this payment',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={!!payment} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete this payment?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-foreground-muted">
            {payment
              ? `The ${formatMoney(payment.amountCents)} ${(PAYMENT_KIND_LABEL[payment.kind] ?? payment.kind).toLowerCase()} entry`
              : 'This payment'}{' '}
            is permanently removed. Use this only for a payment recorded by mistake — if the
            customer actually paid and it was later reversed, void it instead. This can't be undone.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deletePayment.mutate()}
            disabled={deletePayment.isPending}
          >
            {deletePayment.isPending ? 'Deleting…' : 'Delete payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** For a cancelled rental created by mistake — one that never took a
 *  payment or moved a unit beyond its own creation and cancellation
 *  (t0953). The server re-checks eligibility; this dialog only renders
 *  when `rental.deletable` already agrees. */
function DeleteRentalDialog({
  rental,
  open,
  onClose,
  onDeleted,
}: {
  rental: RentalDetail
  open: boolean
  onClose: () => void
  onDeleted: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const deleteRental = useMutation({
    mutationFn: () => api.delete(`/rentals/${rental.id}`),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: queryKeys.rentals.detail(rental.id) })
      queryClient.removeQueries({ queryKey: queryKeys.rentals.events(rental.id) })
      queryClient.removeQueries({ queryKey: queryKeys.rentals.transitions(rental.id) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      toast.success('Rental deleted')
      onDeleted()
    },
    onError: (error) =>
      toast.error(
        'Could not delete this rental',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete rental {rental.rentalNumber}?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-foreground-muted">
            This rental was cancelled and has no payments or unit history. Deleting it permanently
            removes it and its records — this can&rsquo;t be undone.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteRental.mutate()}
            disabled={deleteRental.isPending}
          >
            {deleteRental.isPending ? 'Deleting…' : 'Delete rental'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * A verification scan found the wrong item (t1105). The follow-up task already
 * exists; this stays up until staff take the flag down.
 */
function AttentionBanner({
  rentalId,
  reason,
  canWrite,
}: {
  rentalId: string
  reason: string | null
  canWrite: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const clear = useMutation({
    mutationFn: () => api.post(`/rentals/${rentalId}/clear-attention`, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      toast.success('Flag cleared')
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not clear the flag'),
  })

  return (
    <Alert tone="danger" className="mb-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>
            <strong className="font-semibold">Needs attention.</strong>{' '}
            {reason ?? 'A return scan did not match.'} A follow-up task was created to contact the
            customer and check the camera footage.
          </span>
        </p>
        {canWrite ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={clear.isPending}
            onClick={() => clear.mutate()}
          >
            Clear flag
          </Button>
        ) : null}
      </div>
    </Alert>
  )
}

const SCAN_STEP_LABEL: Record<RentalReturnScan['step'], string> = {
  announce: 'Customer at kiosk',
  receive: 'Received',
  verify: 'Verified',
}

/**
 * The three-step return (t1105). The customer's kiosk scan and staff's receipt
 * scan are recorded as they happen; here staff scan each item to verify it is
 * the one rented. The last verified item returns the rental by itself. A USB
 * scanner types the code and presses Enter.
 */
function ReturnVerificationCard({ rental, canWrite }: { rental: RentalDetail; canWrite: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [code, setCode] = useState('')
  const [feedback, setFeedback] = useState<{
    tone: 'success' | 'warning' | 'danger'
    text: string
  } | null>(null)

  const openItems = rental.items.filter((item) => !item.releasedAt)
  const verifiedCount = openItems.filter((item) => item.verifiedAt).length

  const { data: scans } = useQuery({
    queryKey: queryKeys.rentals.returnScans(rental.id),
    queryFn: () => api.get<RentalReturnScan[]>(`/rentals/${rental.id}/return-scans`),
  })

  const verify = useMutation({
    mutationFn: (scanned: string) =>
      api.post<RentalVerifyScanResult>(`/rentals/${rental.id}/verify-scan`, { code: scanned }),
    onSuccess: async (result) => {
      setCode('')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() }),
        result.completed
          ? queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
          : null,
      ])
      if (result.completed) toast.success(`All items verified — ${rental.rentalNumber} returned`)
      setFeedback({
        tone:
          result.result === 'matched'
            ? 'success'
            : result.result === 'already_scanned'
              ? 'warning'
              : 'danger',
        text: result.message,
      })
      inputRef.current?.focus()
    },
    onError: (err) => {
      setCode('')
      setFeedback({
        tone: 'danger',
        text: err instanceof ApiError ? err.message : 'That scan did not go through.',
      })
      inputRef.current?.focus()
    },
  })

  const stamp = (at: string | null, label: string) => (
    <li className="flex items-center justify-between gap-3">
      <span>{label}</span>
      {at ? (
        <span className="text-foreground-muted">
          {new Date(at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
      ) : (
        <span className="text-foreground-subtle">Not yet</span>
      )}
    </li>
  )

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <ScanLine className="size-4" aria-hidden="true" /> Return verification
        </CardTitle>
        <span className="text-sm text-foreground-muted">
          {verifiedCount} of {openItems.length} verified
        </span>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <ul className="space-y-1">
          {stamp(rental.returnAnnouncedAt, 'Customer scanned at the kiosk')}
          {stamp(rental.returnReceivedAt, 'Staff marked received')}
        </ul>

        <ul className="divide-y divide-border">
          {openItems.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="truncate font-medium">{item.productName}</p>
                <p className="font-identifier text-2xs text-foreground-muted">{item.imei}</p>
              </div>
              {item.verifiedAt ? (
                <Badge tone="success">Verified</Badge>
              ) : (
                <Badge tone="neutral">Awaiting scan</Badge>
              )}
            </li>
          ))}
        </ul>

        {canWrite ? (
          <div className="space-y-2">
            <Input
              ref={inputRef}
              identifier
              autoComplete="off"
              value={code}
              disabled={verify.isPending}
              placeholder="Scan an item to verify it"
              aria-label="Scan an item to verify it"
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                const scanned = code.trim()
                if (scanned) verify.mutate(scanned)
              }}
            />
            {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}
            <p className="text-2xs text-foreground-muted">
              Scanning every item returns the rental. The deposit is not refunded automatically.
            </p>
          </div>
        ) : null}

        {scans && scans.length > 0 ? (
          <details>
            <summary className="cursor-pointer text-2xs uppercase tracking-wide text-foreground-subtle">
              Recent scans ({scans.length})
            </summary>
            <ul className="mt-2 divide-y divide-border">
              {scans.map((scan) => (
                <li key={scan.id} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="min-w-0">
                    <span className="font-medium">{SCAN_STEP_LABEL[scan.step]}</span>{' '}
                    <span className="font-identifier text-2xs text-foreground-muted">
                      {scan.code}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <Badge
                      tone={
                        scan.result === 'matched'
                          ? 'success'
                          : scan.result === 'already_scanned'
                            ? 'neutral'
                            : 'danger'
                      }
                    >
                      {humanizeStatus(scan.result)}
                    </Badge>
                    <span className="text-2xs text-foreground-muted">
                      {new Date(scan.createdAt).toLocaleTimeString('en-US', { timeStyle: 'short' })}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </CardContent>
    </Card>
  )
}
